import React, { useEffect, useState } from 'react';
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "@/hooks/use-toast";
import { useUserName } from '@/hooks/useUserName';
import { useUserEmail } from '@/hooks/useUserEmail';
import { useTagValidation } from '@/hooks/useTagValidation';
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { supabase } from '@/integrations/supabase/client';
import { createCanonicalStructure } from '@/utils/canonicalStructure';
import { SolutionData, SolutionDetails, SolutionMetadata } from '@/types/solution';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { CheckCircle } from "lucide-react";
import RawContentTab from "@/components/solution/RawContentTab";
import { ImageUpload } from "@/components/ImageUpload";
import DeleteSolutionMenu from "@/components/DeleteSolutionMenu";
import { getCurrentTags, syncTagsToSolution } from '@/services/tagService';
import { useIntelligentFullMetadataSync } from '@/hooks/useMetadataSync';
import TagValidationDialog from '@/components/TagValidationDialog';

const formSchema = z.object({
  suggesterName: z.string().min(1, "Name is required"),
  email: z.string().email("Invalid email address"),
  jsonContent: z.string()
    .min(2, "JSON content is required")
    .refine((val) => {
      try {
        JSON.parse(val);
        return true;
      } catch (e) {
        return false;
      }
    }, "Must be valid JSON"),
  solutionTitle: z.string().min(1, "Solution title is required"),
  solutionShortDescription: z.string().min(1, "Solution short description is required"),
  solutionTags: z.string().optional(),
  imageUrl: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface SuggestEditFormProps {
  solutionId: string;
  solutionName: string;
  solutionShortDescription: string;
  solutionImage: string;
  solutionDetails: SolutionDetails; 
  onSuccess: () => void;
}

const SuggestEditForm = ({ 
  solutionId, 
  solutionName, 
  solutionShortDescription,
  solutionImage,
  solutionDetails, 
  onSuccess 
}: SuggestEditFormProps) => {
  const [currentTags, setCurrentTags] = useState<string[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(true);
  const [activeTab, setActiveTab] = useState('basicInfo');
  
  // Get persistent user name and email
  const { userName, setUserName } = useUserName();
  const { userEmail, setUserEmail } = useUserEmail();
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      suggesterName: userName || "",
      email: userEmail || "",
      jsonContent: "",
      solutionTitle: solutionName,
      solutionShortDescription: solutionShortDescription,
      solutionTags: "",
      imageUrl: solutionImage || "",
    },
  });

  // Update form when persistent user name or email changes
  useEffect(() => {
    if (userName) {
      form.setValue('suggesterName', userName);
    }
    if (userEmail) {
      form.setValue('email', userEmail);
    }
  }, [userName, userEmail, form]);

  // Watch for changes to persist user data
  const watchedUserName = form.watch('suggesterName');
  const watchedEmail = form.watch('email');

  useEffect(() => {
    if (watchedUserName && watchedUserName !== userName) {
      setUserName(watchedUserName);
    }
  }, [watchedUserName, userName, setUserName]);

  useEffect(() => {
    if (watchedEmail && watchedEmail !== userEmail) {
      setUserEmail(watchedEmail);
    }
  }, [watchedEmail, userEmail, setUserEmail]);

  // Fetch current tags on mount
  useEffect(() => {
    const fetchCurrentTags = async () => {
      try {
        const tags = await getCurrentTags(supabase, solutionId);
        setCurrentTags(tags);
      } catch (error) {
        console.error('Error fetching current tags:', error);
      } finally {
        setIsLoadingTags(false);
      }
    };

    fetchCurrentTags();
  }, [solutionId]);

  // Initialize JSON content if not already present
  useEffect(() => {
    if (solutionDetails && !form.getValues('jsonContent')) {
      form.setValue('jsonContent', JSON.stringify(solutionDetails, null, 2));
    }
  }, [solutionDetails, form]);

  // Initialize tags field with current tags
  useEffect(() => {
    if (currentTags.length > 0 && !form.getValues('solutionTags')) {
      form.setValue('solutionTags', currentTags.join(', '));
    }
  }, [currentTags, form]);

  // Use intelligent metadata sync
  const { syncMetadata } = useIntelligentFullMetadataSync(form, {
    jsonContentFieldName: 'jsonContent',
    sampleMetadata: {
      name: solutionName,
      shortDescription: solutionShortDescription,
      tags: currentTags,
      imageUrl: solutionImage
    }
  });

  const [submitted, setSubmitted] = React.useState(false);
  
  // Tag validation states
  const [showTagValidation, setShowTagValidation] = useState(false);
  const [pendingSubmissionData, setPendingSubmissionData] = useState<any>(null);
  
  const { validateTags, isValidating } = useTagValidation();

  // Handle tab changes - sync metadata when switching tabs
  const handleTabChange = (newTab: string) => {
    syncMetadata(); // Sync based on last modified source
    setActiveTab(newTab);
  };

  const onSubmit = async (data: FormValues) => {
    try {
      // Sync metadata before submission to ensure consistency
      syncMetadata();
      
      // Get the latest values after sync
      const latestData = form.getValues();
      
      // Parse JSON to validate
      const parsedJson = JSON.parse(latestData.jsonContent);
      
      // Parse tags from input (but don't sync to database yet - wait for admin approval)
      const newTags = latestData.solutionTags 
        ? latestData.solutionTags.split(',').map(tag => tag.trim()).filter(Boolean)
        : [];
      
      // Validate tags before submission
      try {
        const validationResult = await validateTags(newTags);
        
        if (validationResult.hasNewTags) {
          // Store submission data and show validation dialog
          setPendingSubmissionData({
            latestData,
            parsedJson,
            validationResult
          });
          setShowTagValidation(true);
          return;
        }
        
        // No new tags, proceed with normal submission
        await proceedWithSubmission(latestData, parsedJson);
      } catch (error) {
        console.error('Tag validation error:', error);
        // If validation fails, proceed with submission anyway
        await proceedWithSubmission(latestData, parsedJson);
      }
      
    } catch (error) {
      console.error('Error submitting suggestion:', error);
      toast({
        title: "Submission failed",
        description: "There was an error submitting your suggestion. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  const proceedWithSubmission = async (latestData: FormValues, parsedJson: any) => {
    // Create canonical structure using utility function
    // Don't destructure the parsedJson - pass it directly as details
    const canonicalStructure = createCanonicalStructure({
      name: latestData.solutionTitle,
      shortDescription: latestData.solutionShortDescription,
      tags: latestData.solutionTags,
      imageUrl: latestData.imageUrl,
      details: parsedJson
    });
    
    const { error } = await supabase
      .from('edit_suggestions')
      .insert({
        solution_id: solutionId,
        suggester_name: latestData.suggesterName,
        email: latestData.email,
        suggestion: JSON.stringify(canonicalStructure)
      });
    
    if (error) throw error;
    
    // Show success toast
    toast({
      title: "Suggestion submitted",
      description: `Thank you for your input on ${solutionName}. We'll review it shortly.`,
      variant: "default",
    });
    
    // Show success message in component
    setSubmitted(true);
    
    // Reset form and close dialog after a delay - changed from 5 seconds to 10 seconds
    setTimeout(() => {
      form.reset();
      onSuccess();
    }, 10000);
  };
  
  const handleTagValidationConfirm = async () => {
    if (pendingSubmissionData) {
      setShowTagValidation(false);
      await proceedWithSubmission(pendingSubmissionData.latestData, pendingSubmissionData.parsedJson);
      setPendingSubmissionData(null);
    }
  };
  
  const handleTagValidationCancel = () => {
    setShowTagValidation(false);
    setPendingSubmissionData(null);
  };

  if (submitted) {
    return (
      <Alert className="bg-green-50 border-green-200 text-green-800 my-4">
        <CheckCircle className="h-5 w-5 text-green-600" />
        <AlertTitle>Success!</AlertTitle>
        <AlertDescription>
          Your suggestion has been submitted successfully. Thank you for your contribution!
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {/* User Information Section */}
          <div className="space-y-4 mb-6 border-b pb-6">
            <h3 className="text-lg font-medium">Your Information</h3>
            <FormField
              control={form.control}
              name="suggesterName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Your Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Address</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="Enter your email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="basicInfo">Basic Info</TabsTrigger>
              <TabsTrigger value="rawContent">Raw Content</TabsTrigger>
            </TabsList>

            <TabsContent value="basicInfo" className="space-y-4">
              <FormField
                control={form.control}
                name="solutionTitle"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Solution Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter solution title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="solutionShortDescription"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Short Description</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Enter a brief description"
                        className="min-h-[80px]"
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="imageUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Solution Image</FormLabel>
                    <FormControl>
                                             <ImageUpload
                         value={field.value || ""}
                         onChange={field.onChange}
                         className="mt-2"
                       />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="solutionTags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tags</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="Enter tags separated by commas" 
                        {...field} 
                      />
                    </FormControl>
                    <FormDescription className="text-xs text-muted-foreground mt-1">
                      Enter tags separated by commas.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </TabsContent>

                         <TabsContent value="rawContent">
               <RawContentTab
                 jsonContent={form.watch('jsonContent')}
                 setJsonContent={(content) => form.setValue('jsonContent', content)}
                 solutionName={form.watch('solutionTitle')}
                 solutionShortDescription={form.watch('solutionShortDescription')}
                 solutionTags={form.watch('solutionTags')?.split(',').map(tag => tag.trim()).filter(Boolean)}
                 imageUrl={form.watch('imageUrl')}
               />
             </TabsContent>
          </Tabs>
          
          {/* Submit Button */}
          <div className="sticky bottom-0 bg-background py-4 border-t">
            <div className="flex justify-start sm:justify-end px-4 sm:px-6 py-2 overflow-x-auto">
              <div className="flex items-center gap-2 min-w-max">
                <DeleteSolutionMenu
                  solutionId={solutionId}
                  solutionName={solutionName}
                  solutionDetails={solutionDetails}
                  onDeleteSuccess={onSuccess}
                />
                <Button type="button" variant="outline" onClick={onSuccess}>
                  Cancel
                </Button>
                <Button type="submit" disabled={form.formState.isSubmitting || isValidating}>
                  {(form.formState.isSubmitting || isValidating) ? "Processing..." : "Submit Suggestion"}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </Form>
      
      {/* Tag Validation Dialog */}
      {pendingSubmissionData && (
        <TagValidationDialog
          open={showTagValidation}
          onOpenChange={setShowTagValidation}
          newTags={pendingSubmissionData.validationResult?.newTags || []}
          existingTags={pendingSubmissionData.validationResult?.existingTags || []}
          onConfirm={handleTagValidationConfirm}
          onCancel={handleTagValidationCancel}
          context="submission"
          isLoading={form.formState.isSubmitting}
        />
      )}
    </>
  );
};

export default SuggestEditForm;
